import React, { useEffect, useState } from 'react';
import { Alert, TextField } from '@mui/material';
import ConfirmationDialog, { ConfirmFacts } from '../../common/ConfirmationDialog';

interface PublishDialogProps {
  open: boolean;
  onClose: () => void;
  onPublish: (note: string) => Promise<void>;
  placed: number;
  required: number;
  replacesLive: boolean;
}

/** Make the draft the live timetable. */
const PublishDialog: React.FC<PublishDialogProps> = ({ open, onClose, onPublish, placed, required, replacesLive }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const missing = Math.max(0, required - placed);

  useEffect(() => { if (open) { setNote(''); setError(null); } }, [open]);

  const publish = async () => {
    setBusy(true);
    setError(null);
    try {
      await onPublish(note.trim());
      onClose();
    } catch (err: any) {
      setError(err?.message || 'The timetable was not published.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmationDialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      onConfirm={publish}
      title="Publish timetable"
      message={replacesLive
        ? 'Teachers and classes will see this timetable from now on. The current one is kept in history.'
        : 'Teachers and classes will see this timetable from now on.'}
      confirmText="Publish"
      confirmColor="primary"
      loading={busy}
    >
      <ConfirmFacts items={[{ label: 'Periods placed', value: `${placed} of ${required}` }]} />
      {missing > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {missing} period{missing === 1 ? ' is' : 's are'} not placed yet. Those classes will have free periods instead.
        </Alert>
      )}
      <TextField
        label="Note (optional)"
        placeholder="e.g. Winter timings"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        fullWidth
        size="small"
        sx={{ mt: 2 }}
        inputProps={{ maxLength: 300 }}
        disabled={busy}
      />
      {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
    </ConfirmationDialog>
  );
};

export default PublishDialog;
